import type { MockStore, MockStoreState } from "./types";

import { enableMapSet } from "immer";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

// The mock store keeps conversations/messages/activities in Maps and mutates
// them inside Immer producers, which requires Immer's MapSet plugin.
enableMapSet();

const initialState: MockStoreState = {
  initialized: false,
  organization: null,
  workspaces: [],
  users: [],
  currentUserId: null,
  channels: [],
  contacts: [],
  conversations: new Map(),
  messages: new Map(),
  activities: new Map(),
  lifecycles: [],
  tags: [],
  doctors: [],
  treatments: [],
  departments: [],
  appointments: [],
  invoices: [],
  receipts: [],
  advances: [],
  billingSyncRuns: [],
  products: [],
  variantAttributes: [],
  categories: [],
  brands: [],
  discounts: [],
  orders: [],
  tickets: [],
  snippets: [],
  teams: [],
  forms: [],
  websites: [],
  segments: [],
  segmentExports: [],
  closingNotes: [],
  savedFiles: [],
  wrapUps: [],
  customViews: [],
  adAccounts: [],
  ads: [],
  broadcasts: [],
  workflows: [],
  workflowExecutions: [],
  knowledgeBaseEntries: [],
  knowledgeSuggestions: [],
};

export const useMockStore = create<MockStore>()(
  immer((set) => ({
    ...initialState,

    // Initialization
    setInitialized: (value) =>
      set((state) => {
        state.initialized = value;
      }),

    // Organization
    setOrganization: (org) =>
      set((state) => {
        state.organization = org;
      }),
    updateOrganization: (updates) =>
      set((state) => {
        if (state.organization) {
          Object.assign(state.organization, updates);
        }
      }),

    // Workspaces
    setWorkspaces: (workspaces) =>
      set((state) => {
        state.workspaces = workspaces;
      }),
    addWorkspace: (workspace) =>
      set((state) => {
        state.workspaces.push(workspace);
      }),
    updateWorkspace: (id, updates) =>
      set((state) => {
        const index = state.workspaces.findIndex((w) => w.id === id);

        if (index !== -1) {
          Object.assign(state.workspaces[index], updates);
        }
      }),

    // Users
    setUsers: (users) =>
      set((state) => {
        state.users = users;
      }),
    setCurrentUserId: (id) =>
      set((state) => {
        state.currentUserId = id;
      }),
    addUser: (user) =>
      set((state) => {
        state.users.push(user);
      }),
    updateUser: (id, updates) =>
      set((state) => {
        const index = state.users.findIndex((u) => u.id === id);

        if (index !== -1) {
          Object.assign(state.users[index], updates);
        }
      }),
    deleteUser: (id) =>
      set((state) => {
        state.users = state.users.filter((u) => u.id !== id);
      }),

    // Channels
    setChannels: (channels) =>
      set((state) => {
        state.channels = channels;
      }),
    addChannel: (channel) =>
      set((state) => {
        state.channels.push(channel);
      }),
    updateChannel: (id, updates) =>
      set((state) => {
        const index = state.channels.findIndex((c) => c.id === id);

        if (index !== -1) {
          Object.assign(state.channels[index], updates);
        }
      }),
    deleteChannel: (id) =>
      set((state) => {
        state.channels = state.channels.filter((c) => c.id !== id);
      }),

    // Contacts
    setContacts: (contacts) =>
      set((state) => {
        state.contacts = contacts;
      }),
    addContact: (contact) =>
      set((state) => {
        state.contacts.push(contact);
      }),
    updateContact: (id, updates) =>
      set((state) => {
        const index = state.contacts.findIndex((c) => c.id === id);

        if (index !== -1) {
          Object.assign(state.contacts[index], updates);
        }
        const conversation = state.conversations.get(id);
        if (conversation) Object.assign(conversation.contact, updates);
      }),
    deleteContact: (id) =>
      set((state) => {
        state.contacts = state.contacts.filter((c) => c.id !== id);
        state.messages.delete(id);
        state.activities.delete(id);
        state.conversations.delete(id);
      }),

    // Conversations
    setConversations: (conversations) =>
      set((state) => {
        state.conversations = conversations;
      }),
    addConversation: (contactId, conversation) =>
      set((state) => {
        state.conversations.set(contactId, conversation);
      }),
    updateConversation: (contactId, updates) =>
      set((state) => {
        const conversation = state.conversations.get(contactId);

        if (conversation) {
          state.conversations.set(contactId, { ...conversation, ...updates });
        }
      }),

    // Messages
    setMessages: (messages) =>
      set((state) => {
        state.messages = messages;
      }),
    addMessage: (contactId, message) =>
      set((state) => {
        const existing = state.messages.get(contactId) || [];

        state.messages.set(contactId, [...existing, message]);
      }),

    // Activities
    setActivities: (activities) =>
      set((state) => {
        state.activities = activities;
      }),
    addActivity: (contactId, activity) =>
      set((state) => {
        const existing = state.activities.get(contactId) || [];

        state.activities.set(contactId, [...existing, activity]);
      }),

    // Lifecycles
    setLifecycles: (lifecycles) =>
      set((state) => {
        state.lifecycles = lifecycles;
      }),
    addLifecycle: (lifecycle) =>
      set((state) => {
        state.lifecycles.push(lifecycle);
      }),
    updateLifecycle: (id, updates) =>
      set((state) => {
        const index = state.lifecycles.findIndex((l) => l.id === id);

        if (index !== -1) {
          Object.assign(state.lifecycles[index], updates);
        }
      }),
    deleteLifecycle: (id) =>
      set((state) => {
        state.lifecycles = state.lifecycles.filter((l) => l.id !== id);
      }),

    // Tags
    setTags: (tags) =>
      set((state) => {
        state.tags = tags;
      }),
    addTag: (tag) =>
      set((state) => {
        state.tags.push(tag);
      }),
    updateTag: (id, updates) =>
      set((state) => {
        const index = state.tags.findIndex((t) => t.id === id);

        if (index !== -1) {
          Object.assign(state.tags[index], updates);
        }
      }),
    deleteTag: (id) =>
      set((state) => {
        state.tags = state.tags.filter((t) => t.id !== id);
      }),

    // Doctors
    setDoctors: (doctors) =>
      set((state) => {
        state.doctors = doctors;
      }),
    addDoctor: (doctor) =>
      set((state) => {
        state.doctors.push(doctor);
      }),
    updateDoctor: (id, updates) =>
      set((state) => {
        const index = state.doctors.findIndex((d) => d.id === id);

        if (index !== -1) {
          Object.assign(state.doctors[index], updates);
        }
      }),
    deleteDoctor: (id) =>
      set((state) => {
        state.doctors = state.doctors.filter((d) => d.id !== id);
      }),

    // Treatments
    setTreatments: (treatments) =>
      set((state) => {
        state.treatments = treatments;
      }),
    addTreatment: (treatment) =>
      set((state) => {
        state.treatments.push(treatment);
      }),
    updateTreatment: (id, updates) =>
      set((state) => {
        const index = state.treatments.findIndex((t) => t.id === id);

        if (index !== -1) {
          Object.assign(state.treatments[index], updates);
        }
      }),
    deleteTreatment: (id) =>
      set((state) => {
        state.treatments = state.treatments.filter((t) => t.id !== id);
      }),

    // Departments
    setDepartments: (departments) =>
      set((state) => {
        state.departments = departments;
      }),
    addDepartment: (department) =>
      set((state) => {
        state.departments.push(department);
      }),
    updateDepartment: (id, updates) =>
      set((state) => {
        const index = state.departments.findIndex((d) => d.id === id);

        if (index !== -1) {
          Object.assign(state.departments[index], updates);
        }
      }),
    deleteDepartment: (id) =>
      set((state) => {
        state.departments = state.departments.filter((d) => d.id !== id);
      }),

    // Appointments
    setAppointments: (appointments) =>
      set((state) => {
        state.appointments = appointments;
      }),
    addAppointment: (appointment) =>
      set((state) => {
        state.appointments.push(appointment);
      }),
    updateAppointment: (id, updates) =>
      set((state) => {
        const index = state.appointments.findIndex((a) => a.id === id);

        if (index !== -1) {
          Object.assign(state.appointments[index], updates);
        }
      }),
    deleteAppointment: (id) =>
      set((state) => {
        state.appointments = state.appointments.filter((a) => a.id !== id);
      }),

    // Invoices
    setInvoices: (invoices) =>
      set((state) => {
        state.invoices = invoices;
      }),
    addInvoice: (invoice) =>
      set((state) => {
        state.invoices.push(invoice);
      }),
    updateInvoice: (id, updates) =>
      set((state) => {
        const index = state.invoices.findIndex((i) => i.id === id);

        if (index !== -1) {
          Object.assign(state.invoices[index], updates);
        }
      }),
    deleteInvoice: (id) =>
      set((state) => {
        state.invoices = state.invoices.filter((i) => i.id !== id);
      }),

    // Receipts & advances
    setReceipts: (receipts) =>
      set((state) => {
        state.receipts = receipts;
      }),
    addReceipt: (receipt) =>
      set((state) => {
        state.receipts.push(receipt);
      }),
    updateReceipt: (id, updates) =>
      set((state) => {
        const index = state.receipts.findIndex((r) => r.id === id);

        if (index !== -1) {
          Object.assign(state.receipts[index], updates);
        }
      }),
    setAdvances: (advances) =>
      set((state) => {
        state.advances = advances;
      }),
    addAdvance: (advance) =>
      set((state) => {
        state.advances.push(advance);
      }),
    updateAdvance: (id, updates) =>
      set((state) => {
        const index = state.advances.findIndex((a) => a.id === id);

        if (index !== -1) {
          Object.assign(state.advances[index], updates);
        }
      }),

    // Billing sync runs
    setBillingSyncRuns: (runs) =>
      set((state) => {
        state.billingSyncRuns = runs;
      }),
    addBillingSyncRun: (run) =>
      set((state) => {
        state.billingSyncRuns.push(run);
      }),
    updateBillingSyncRun: (id, updates) =>
      set((state) => {
        const index = state.billingSyncRuns.findIndex((r) => r.id === id);

        if (index !== -1) {
          Object.assign(state.billingSyncRuns[index], updates);
        }
      }),

    // Products
    setProducts: (products) =>
      set((state) => {
        state.products = products;
      }),
    setVariantAttributes: (attributes) =>
      set((state) => {
        state.variantAttributes = attributes;
      }),
    addProduct: (product) =>
      set((state) => {
        state.products.push(product);
      }),
    updateProduct: (id, updates) =>
      set((state) => {
        const index = state.products.findIndex((p) => p.id === id);

        if (index !== -1) {
          Object.assign(state.products[index], updates);
        }
      }),
    deleteProduct: (id) =>
      set((state) => {
        state.products = state.products.filter((p) => p.id !== id);
      }),

    // Variant Attributes
    addVariantAttribute: (attribute) =>
      set((state) => {
        state.variantAttributes.push(attribute);
      }),
    updateVariantAttribute: (id, updates) =>
      set((state) => {
        const index = state.variantAttributes.findIndex((a) => a.id === id);

        if (index !== -1) {
          Object.assign(state.variantAttributes[index], updates);
        }
      }),
    deleteVariantAttribute: (id) =>
      set((state) => {
        state.variantAttributes = state.variantAttributes.filter(
          (a) => a.id !== id,
        );
      }),
    addAttributeOption: (attributeId, option) =>
      set((state) => {
        const attribute = state.variantAttributes.find(
          (a) => a.id === attributeId,
        );

        if (attribute) {
          if (!attribute.options) {
            attribute.options = [];
          }
          attribute.options.push(option);
        }
      }),
    updateAttributeOption: (attributeId, optionId, updates) =>
      set((state) => {
        const attribute = state.variantAttributes.find(
          (a) => a.id === attributeId,
        );

        if (attribute?.options) {
          const optionIndex = attribute.options.findIndex(
            (o) => o.id === optionId,
          );

          if (optionIndex !== -1) {
            Object.assign(attribute.options[optionIndex], updates);
          }
        }
      }),
    deleteAttributeOption: (attributeId, optionId) =>
      set((state) => {
        const attribute = state.variantAttributes.find(
          (a) => a.id === attributeId,
        );

        if (attribute?.options) {
          attribute.options = attribute.options.filter(
            (o) => o.id !== optionId,
          );
        }
      }),

    // Categories
    setCategories: (categories) =>
      set((state) => {
        state.categories = categories;
      }),
    addCategory: (category) =>
      set((state) => {
        state.categories.push(category);
      }),
    updateCategory: (id, updates) =>
      set((state) => {
        const index = state.categories.findIndex((c) => c.id === id);

        if (index !== -1) {
          Object.assign(state.categories[index], updates);
        }
      }),
    deleteCategory: (id) =>
      set((state) => {
        state.categories = state.categories.filter((c) => c.id !== id);
      }),

    // Brands
    setBrands: (brands) =>
      set((state) => {
        state.brands = brands;
      }),
    addBrand: (brand) =>
      set((state) => {
        state.brands.push(brand);
      }),
    updateBrand: (id, updates) =>
      set((state) => {
        const index = state.brands.findIndex((b) => b.id === id);

        if (index !== -1) {
          Object.assign(state.brands[index], updates);
        }
      }),
    deleteBrand: (id) =>
      set((state) => {
        state.brands = state.brands.filter((b) => b.id !== id);
      }),

    // Discounts
    setDiscounts: (discounts) =>
      set((state) => {
        state.discounts = discounts;
      }),
    addDiscount: (discount) =>
      set((state) => {
        state.discounts.push(discount);
      }),
    updateDiscount: (id, updates) =>
      set((state) => {
        const index = state.discounts.findIndex((d) => d.id === id);

        if (index !== -1) {
          Object.assign(state.discounts[index], updates);
        }
      }),
    deleteDiscount: (id) =>
      set((state) => {
        state.discounts = state.discounts.filter((d) => d.id !== id);
      }),

    // Orders
    setOrders: (orders) =>
      set((state) => {
        state.orders = orders;
      }),
    addOrder: (order) =>
      set((state) => {
        state.orders.push(order);
      }),
    updateOrder: (id, updates) =>
      set((state) => {
        const index = state.orders.findIndex((o) => o.id === id);

        if (index !== -1) {
          Object.assign(state.orders[index], updates);
        }
      }),
    deleteOrder: (id) =>
      set((state) => {
        state.orders = state.orders.filter((o) => o.id !== id);
      }),

    // Tickets
    setTickets: (tickets) =>
      set((state) => {
        state.tickets = tickets;
      }),
    addTicket: (ticket) =>
      set((state) => {
        state.tickets.push(ticket);
      }),
    updateTicket: (id, updates) =>
      set((state) => {
        const index = state.tickets.findIndex((t) => t.id === id);

        if (index !== -1) {
          Object.assign(state.tickets[index], updates);
        }
      }),
    deleteTicket: (id) =>
      set((state) => {
        state.tickets = state.tickets.filter((t) => t.id !== id);
      }),

    // Snippets
    setSnippets: (snippets) =>
      set((state) => {
        state.snippets = snippets;
      }),
    addSnippet: (snippet) =>
      set((state) => {
        state.snippets.push(snippet);
      }),
    updateSnippet: (id, updates) =>
      set((state) => {
        const index = state.snippets.findIndex((s) => s.id === id);

        if (index !== -1) {
          Object.assign(state.snippets[index], updates);
        }
      }),
    deleteSnippet: (id) =>
      set((state) => {
        state.snippets = state.snippets.filter((s) => s.id !== id);
      }),

    // Teams
    setTeams: (teams) =>
      set((state) => {
        state.teams = teams;
      }),
    addTeam: (team) =>
      set((state) => {
        state.teams.push(team);
      }),
    updateTeam: (id, updates) =>
      set((state) => {
        const index = state.teams.findIndex((t) => t.id === id);

        if (index !== -1) {
          Object.assign(state.teams[index], updates);
        }
      }),
    deleteTeam: (id) =>
      set((state) => {
        state.teams = state.teams.filter((t) => t.id !== id);
      }),
    addTeamMember: (teamId, member) =>
      set((state) => {
        const team = state.teams.find((t) => t.id === teamId);

        if (team && !team.members.some((m) => m.id === member.id)) {
          team.members.push(member);
          team.memberCount = team.members.length;
        }
      }),
    removeTeamMember: (teamId, userId) =>
      set((state) => {
        const team = state.teams.find((t) => t.id === teamId);

        if (team) {
          team.members = team.members.filter((m) => m.id !== userId);
          team.memberCount = team.members.length;
        }
      }),

    // Acquisition & inbox config
    setForms: (forms) =>
      set((state) => {
        state.forms = forms;
      }),
    setWebsites: (websites) =>
      set((state) => {
        state.websites = websites;
      }),
    setSegments: (segments) =>
      set((state) => {
        state.segments = segments;
      }),
    setSegmentExports: (exports) =>
      set((state) => {
        state.segmentExports = exports;
      }),
    addSegment: (segment) =>
      set((state) => {
        state.segments.unshift(segment);
      }),
    updateSegment: (id, updates) =>
      set((state) => {
        const index = state.segments.findIndex((s) => s.id === id);

        if (index !== -1) {
          Object.assign(state.segments[index], updates);
        }
      }),
    deleteSegment: (id) =>
      set((state) => {
        state.segments = state.segments.filter((s) => s.id !== id);
      }),
    setClosingNotes: (closingNotes) =>
      set((state) => {
        state.closingNotes = closingNotes;
      }),
    setSavedFiles: (savedFiles) =>
      set((state) => {
        state.savedFiles = savedFiles;
      }),
    setWrapUps: (wrapUps) =>
      set((state) => {
        state.wrapUps = wrapUps;
      }),
    setCustomViews: (customViews) =>
      set((state) => {
        state.customViews = customViews;
      }),
    addCustomView: (customView) =>
      set((state) => {
        state.customViews.push(customView);
      }),
    updateCustomView: (id, updates) =>
      set((state) => {
        const index = state.customViews.findIndex((inbox) => inbox.id === id);

        if (index !== -1) {
          Object.assign(state.customViews[index], updates);
        }
      }),
    deleteCustomView: (id) =>
      set((state) => {
        state.customViews = state.customViews.filter(
          (inbox) => inbox.id !== id,
        );
      }),

    // Advertising & broadcasts
    setAdAccounts: (adAccounts) =>
      set((state) => {
        state.adAccounts = adAccounts;
      }),
    setAds: (ads) =>
      set((state) => {
        state.ads = ads;
      }),
    setBroadcasts: (broadcasts) =>
      set((state) => {
        state.broadcasts = broadcasts;
      }),

    // Automation
    setWorkflows: (workflows) =>
      set((state) => {
        state.workflows = workflows;
      }),
    setWorkflowExecutions: (executions) =>
      set((state) => {
        state.workflowExecutions = executions;
      }),

    // AI agent knowledge
    setKnowledgeBaseEntries: (entries) =>
      set((state) => {
        state.knowledgeBaseEntries = entries;
      }),
    addKnowledgeBaseEntry: (entry) =>
      set((state) => {
        state.knowledgeBaseEntries.unshift(entry);
      }),
    updateKnowledgeBaseEntry: (id, updates) =>
      set((state) => {
        const index = state.knowledgeBaseEntries.findIndex((e) => e.id === id);

        if (index !== -1) {
          Object.assign(state.knowledgeBaseEntries[index], updates);
        }
      }),
    deleteKnowledgeBaseEntry: (id) =>
      set((state) => {
        state.knowledgeBaseEntries = state.knowledgeBaseEntries.filter(
          (e) => e.id !== id,
        );
      }),

    setKnowledgeSuggestions: (suggestions) =>
      set((state) => {
        state.knowledgeSuggestions = suggestions;
      }),
    updateKnowledgeSuggestion: (id, updates) =>
      set((state) => {
        const index = state.knowledgeSuggestions.findIndex((s) => s.id === id);

        if (index !== -1) {
          Object.assign(state.knowledgeSuggestions[index], updates);
        }
      }),
    deleteKnowledgeSuggestion: (id) =>
      set((state) => {
        state.knowledgeSuggestions = state.knowledgeSuggestions.filter(
          (s) => s.id !== id,
        );
      }),

    // Reset
    reset: () =>
      set(() => ({
        ...initialState,
        conversations: new Map(),
        messages: new Map(),
        activities: new Map(),
      })),
  })),
);
